import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { api, formatCurrency } from '../../utils/api'
import { MetricsRow, SearchLinks, ProgressBar } from '../shared'

function GoogleShoppingListings() {
  const [listings, setListings] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [error, setError] = useState(null)
  const [statusCounts, setStatusCounts] = useState({})
  const [approvalStatusCounts, setApprovalStatusCounts] = useState({})
  const [syncState, setSyncState] = useState(null)
  const [selectedItems, setSelectedItems] = useState(new Set())
  const [processing, setProcessing] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0, status: '' })
  const [expandedIssues, setExpandedIssues] = useState(null)
  const abortControllerRef = useRef(null)

  // Search filters
  const [searchFilters, setSearchFilters] = useState({
    search: '',
    status: 'all',
    approval_status: 'all'
  })

  // Display options
  const [sortBy, setSortBy] = useState('updated_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const [limit, setLimit] = useState(100)

  useEffect(() => {
    loadSyncState()
  }, [])

  async function loadSyncState() {
    try {
      const response = await api.get('/api/channels/google/sync-state')
      setSyncState(response)
      setStatusCounts(response.statusCounts || {})
      setApprovalStatusCounts(response.approvalStatusCounts || {})
    } catch (err) {
      console.error('Failed to load sync state:', err)
    }
  }

  async function handleCheckNewListings() {
    setSyncing(true)
    try {
      const result = await api.post('/api/channels/google/check-new-listings')
      const newCount = result.count || 0
      let syncMsg = `Found ${result.totalFound || 0} Google product(s).`
      syncMsg += `\n${newCount} new in-stock listing(s) added.`
      if (result.skippedOutOfStock) syncMsg += `\n${result.skippedOutOfStock} out-of-stock skipped.`
      if (result.statusUpdated) syncMsg += `\n${result.statusUpdated} existing listing(s) updated.`
      alert(syncMsg)
      loadSyncState()
      if (hasSearched) loadListings()
    } catch (err) {
      alert(`Error checking for new listings: ${err.message}`)
    } finally {
      setSyncing(false)
    }
  }

  const loadListings = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (searchFilters.search) params.set('search', searchFilters.search)
      if (searchFilters.status && searchFilters.status !== 'all') params.set('status', searchFilters.status)
      if (searchFilters.approval_status && searchFilters.approval_status !== 'all') params.set('approval_status', searchFilters.approval_status)
      params.set('limit', '500')

      const response = await api.get(`/api/channels/google/live-listings?${params}`)
      setListings(response.listings || [])
      setStatusCounts(response.statusCounts || {})
      setApprovalStatusCounts(response.approvalStatusCounts || {})
      setSelectedItems(new Set())
    } catch (err) {
      console.error('Failed to load Google listings:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [searchFilters])

  function handleFilterChange(field, value) {
    setSearchFilters(prev => ({ ...prev, [field]: value }))
  }

  function handleSearch(e) {
    e.preventDefault()
    setHasSearched(true)
    loadListings()
  }

  function clearFilters() {
    setSearchFilters({ search: '', status: 'all', approval_status: 'all' })
    setSortBy('updated_at')
    setSortOrder('desc')
    setLimit(100)
  }

  // Client-side sorting and pagination
  const displayItems = useMemo(() => {
    const sorted = [...listings].sort((a, b) => {
      let aVal = a[sortBy]
      let bVal = b[sortBy]

      if (['price'].includes(sortBy)) {
        aVal = parseFloat(aVal) || 0
        bVal = parseFloat(bVal) || 0
      }

      if (['quantity'].includes(sortBy)) {
        aVal = parseInt(aVal) || 0
        bVal = parseInt(bVal) || 0
      }

      if (typeof aVal === 'string') aVal = aVal?.toLowerCase() || ''
      if (typeof bVal === 'string') bVal = bVal?.toLowerCase() || ''

      if (['created_at', 'updated_at'].includes(sortBy)) {
        aVal = new Date(aVal).getTime() || 0
        bVal = new Date(bVal).getTime() || 0
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    return sorted.slice(0, limit)
  }, [listings, sortBy, sortOrder, limit])

  function handleSort(field) {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  function SortHeader({ field, children, align }) {
    const isActive = sortBy === field
    const arrow = isActive ? (sortOrder === 'asc' ? ' \u25B2' : ' \u25BC') : ''
    return (
      <th
        onClick={() => handleSort(field)}
        style={{ cursor: 'pointer', textAlign: align, userSelect: 'none' }}
        className={isActive ? 'sort-active' : ''}
      >
        {children}{arrow}
      </th>
    )
  }

  function handleSelectAll(e) {
    if (e.target.checked) {
      setSelectedItems(new Set(displayItems.map(item => item.id)))
    } else {
      setSelectedItems(new Set())
    }
  }

  function handleSelectItem(id) {
    setSelectedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) newSet.delete(id)
      else newSet.add(id)
      return newSet
    })
  }

  async function handleProcessSelected() {
    if (selectedItems.size === 0) return

    const confirmed = window.confirm(
      `Process ${selectedItems.size} listing(s)?\n\n` +
      `This will:\n` +
      `\u2022 Auto-categorize using AI for Google Shopping\n` +
      `\u2022 Determine condition, brand, and color\n` +
      `\u2022 Set Google Shopping product category\n\n` +
      `Continue?`
    )
    if (!confirmed) return

    setProcessing(true)
    abortControllerRef.current = new AbortController()
    const ids = Array.from(selectedItems)
    let completed = 0
    let errors = []
    let successes = []

    for (const id of ids) {
      if (abortControllerRef.current.signal.aborted) break

      const item = listings.find(l => l.id === id)
      if (!item) continue

      setProgress({
        current: completed + 1,
        total: ids.length,
        status: `Processing: ${item.title?.substring(0, 40)}...`
      })

      try {
        const result = await api.post('/api/channels/google/process-item', {
          listing_id: item.id
        })

        if (result.success) {
          successes.push({
            id: item.id,
            title: item.title,
            category: result.google_category,
            condition: result.condition,
            brand: result.brand,
            color: result.color
          })
        } else {
          errors.push({ id: item.id, title: item.title, error: result.error || 'Unknown error' })
        }
        completed++
      } catch (err) {
        errors.push({ id: item.id, title: item.title, error: err.message })
        completed++
      }

      // Small delay between requests
      await new Promise(resolve => setTimeout(resolve, 500))
    }

    setProcessing(false)
    setProgress({ current: 0, total: 0, status: '' })

    // Log full results to console
    console.log('=== GOOGLE SHOPPING PROCESSING RESULTS ===')
    console.log(`Total: ${completed}, Success: ${successes.length}, Failed: ${errors.length}`)
    console.table(successes.map(s => ({
      title: s.title?.substring(0, 60),
      category: s.category || '-',
      condition: s.condition || '-',
      brand: s.brand || '-',
      color: s.color || '-'
    })))
    if (errors.length > 0) {
      console.log('ERRORS:')
      console.table(errors.map(e => ({ title: e.title?.substring(0, 60), error: e.error })))
    }

    // Show summary in alert
    let message = `Processed ${completed} listing(s).\n\n`

    if (successes.length > 0) {
      message += `\u2713 ${successes.length} SUCCESSFUL:\n`
      message += '\u2500'.repeat(40) + '\n'
      successes.slice(0, 10).forEach(s => {
        message += `\n\u2022 ${s.title?.substring(0, 50)}${s.title?.length > 50 ? '...' : ''}\n`
        if (s.category) message += `  Category: ${s.category}\n`
        if (s.condition) message += `  Condition: ${s.condition}\n`
        if (s.brand) message += `  Brand: ${s.brand}\n`
        if (s.color) message += `  Color: ${s.color}\n`
      })
      if (successes.length > 10) {
        message += `\n... and ${successes.length - 10} more (see browser console for full list)\n`
      }
    }

    if (errors.length > 0) {
      message += `\n\u2717 ${errors.length} FAILED:\n`
      message += '\u2500'.repeat(40) + '\n'
      errors.slice(0, 5).forEach(e => {
        message += `\n\u2022 ${e.title?.substring(0, 50)}${e.title?.length > 50 ? '...' : ''}\n`
        message += `  Error: ${e.error}\n`
      })
      if (errors.length > 5) {
        message += `\n... and ${errors.length - 5} more errors\n`
      }
    }

    message += '\nFull results logged to browser console (F12 \u2192 Console)'

    alert(message)
    loadListings()
  }

  function handleCancelOperation() {
    if (abortControllerRef.current) abortControllerRef.current.abort()
  }

  async function handleRequestReview() {
    if (selectedItems.size === 0) return

    const confirmed = window.confirm(
      `Request Google to re-review ${selectedItems.size} listing(s)?\n\n` +
      `This re-submits the product with availability "in_stock" to trigger Google to re-crawl the page.\n\n` +
      `Continue?`
    )
    if (!confirmed) return

    setProcessing(true)
    try {
      const result = await api.post('/api/channels/google/request-review', {
        listing_ids: Array.from(selectedItems)
      })
      let msg = `Re-review requested: ${result.succeeded} succeeded, ${result.failed} failed.`
      if (result.errors?.length > 0) {
        msg += '\n\nErrors:\n'
        result.errors.slice(0, 5).forEach(e => {
          msg += `\n- ${e.title || 'ID ' + e.id}: ${e.error}`
        })
      }
      alert(msg)
      setSelectedItems(new Set())
      loadListings()
    } catch (err) {
      alert(`Error: ${err.message}`)
    } finally {
      setProcessing(false)
    }
  }

  async function handleBulkDelete() {
    if (selectedItems.size === 0) return

    const confirmed = window.confirm(
      `Delete ${selectedItems.size} listing(s) from the database?\n\n` +
      `This only removes them from Prism's tracking list.\n\n` +
      `Continue?`
    )
    if (!confirmed) return

    setProcessing(true)
    try {
      await api.post('/api/channels/google/live-listings/bulk-delete', {
        ids: Array.from(selectedItems)
      })
      alert(`Deleted ${selectedItems.size} listing(s)`)
      setSelectedItems(new Set())
      loadListings()
    } catch (err) {
      alert(`Error: ${err.message}`)
    } finally {
      setProcessing(false)
    }
  }

  async function handleBulkStatusChange(newStatus) {
    if (selectedItems.size === 0) return

    const confirmed = window.confirm(
      `Change status of ${selectedItems.size} listing(s) to "${newStatus}"?`
    )
    if (!confirmed) return

    setProcessing(true)
    try {
      await api.post('/api/channels/google/live-listings/bulk-update', {
        ids: Array.from(selectedItems),
        status: newStatus
      })
      alert(`Updated ${selectedItems.size} listing(s) to ${newStatus}`)
      setSelectedItems(new Set())
      loadListings()
    } catch (err) {
      alert(`Error: ${err.message}`)
    } finally {
      setProcessing(false)
    }
  }

  function getStatusBadge(status) {
    const colors = {
      pending: 'amber',
      processed: 'blue',
      skipped: 'neutral',
      error: 'red'
    }
    return (
      <span className={`badge badge-${colors[status] || 'neutral'}`}>
        {status}
      </span>
    )
  }

  function getApprovalBadge(approvalStatus, item) {
    const config = {
      approved: { color: '#16a34a', bg: '#f0fdf4', label: 'Approved' },
      disapproved: { color: '#dc2626', bg: '#fef2f2', label: 'Disapproved' },
      partially_approved: { color: '#d97706', bg: '#fffbeb', label: 'Partial' },
      pending_review: { color: '#2563eb', bg: '#eff6ff', label: 'Pending' },
      unknown: { color: '#6b7280', bg: '#f9fafb', label: 'Unknown' }
    }
    const c = config[approvalStatus] || config.unknown
    const issues = parseIssues(item)
    const hasIssues = issues.length > 0

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          color: c.color,
          background: c.bg,
          cursor: hasIssues ? 'pointer' : 'default',
          borderBottom: hasIssues ? `1px dashed ${c.color}` : 'none'
        }}
        title={hasIssues ? `${issues.length} issue(s) - click to view` : ''}
        onClick={hasIssues ? () => setExpandedIssues(expandedIssues === item.id ? null : item.id) : undefined}
      >
        {c.label}
        {hasIssues && <span style={{ fontSize: 10 }}>({issues.length})</span>}
      </span>
    )
  }

  function parseIssues(item) {
    if (!item.item_issues) return []
    try {
      return JSON.parse(item.item_issues)
    } catch {
      return []
    }
  }

  function IssuesPanel({ item }) {
    const issues = parseIssues(item)
    if (issues.length === 0) return null

    return (
      <tr>
        <td colSpan={15} style={{ padding: 0 }}>
          <div style={{
            background: '#fef2f2',
            borderTop: '1px solid #fecaca',
            borderBottom: '1px solid #fecaca',
            padding: 'var(--space-3)'
          }}>
            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 'var(--space-2)', color: '#991b1b' }}>
              Issues for: {item.title}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {issues.map((issue, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-2)',
                  background: 'white',
                  borderRadius: 4,
                  fontSize: 12,
                  alignItems: 'flex-start'
                }}>
                  <span style={{
                    padding: '1px 6px',
                    borderRadius: 3,
                    fontWeight: 600,
                    fontSize: 10,
                    textTransform: 'uppercase',
                    flexShrink: 0,
                    color: issue.severity === 'DISAPPROVED' ? '#dc2626' : issue.severity === 'DEMOTED' ? '#d97706' : '#6b7280',
                    background: issue.severity === 'DISAPPROVED' ? '#fef2f2' : issue.severity === 'DEMOTED' ? '#fffbeb' : '#f9fafb'
                  }}>
                    {issue.severity || 'issue'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{issue.description || issue.code}</div>
                    {issue.detail && <div style={{ color: '#6b7280', marginTop: 2 }}>{issue.detail}</div>}
                    {issue.attribute && (
                      <div style={{ color: '#9ca3af', marginTop: 2 }}>
                        Attribute: <code style={{ background: '#f3f4f6', padding: '0 4px', borderRadius: 2 }}>{issue.attribute}</code>
                        {issue.reportingContext && <span> | Context: {issue.reportingContext}</span>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </td>
      </tr>
    )
  }

  const totalCount = Object.values(statusCounts).reduce((a, b) => a + b, 0)
  const pendingCount = listings.filter(l => l.status === 'pending').length
  const disapprovedCount = approvalStatusCounts.disapproved || 0
  const approvedCount = approvalStatusCounts.approved || 0

  const metrics = [
    { label: 'Total', value: totalCount, color: 'blue' },
    { label: 'Pending', value: statusCounts.pending || 0, color: 'amber' },
    { label: 'Processed', value: statusCounts.processed || 0, color: 'green' },
    { label: 'Approved', value: approvedCount, color: 'green' },
    { label: 'Disapproved', value: disapprovedCount, color: 'red' }
  ]

  return (
    <div>
      <MetricsRow metrics={metrics} />

      {/* Sync Status */}
      <div className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
          <div>
            <span className="text-sm" style={{ fontWeight: 500 }}>Last Sync: </span>
            <span className="text-sm text-muted">
              {syncState?.lastSync
                ? new Date(syncState.lastSync).toLocaleString('en-GB')
                : 'Never'
              }
            </span>
            {syncState?.productsFetched > 0 && (
              <span className="text-sm text-muted" style={{ marginLeft: 'var(--space-3)' }}>
                ({syncState.productsFetched} products last fetched)
              </span>
            )}
          </div>
          <button
            className="btn btn-primary"
            onClick={handleCheckNewListings}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync Listings & Status'}
          </button>
        </div>
      </div>

      <form onSubmit={handleSearch}>
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
          <div className="filter-section">
            <div className="filter-group filter-group-wide">
              <label className="filter-label">Search</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search by title, SKU, or product type..."
                value={searchFilters.search}
                onChange={e => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">AI Status</label>
              <select
                className="form-input"
                value={searchFilters.status}
                onChange={e => handleFilterChange('status', e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="processed">Processed</option>
                <option value="skipped">Skipped</option>
                <option value="error">Error</option>
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Approval</label>
              <select
                className="form-input"
                value={searchFilters.approval_status}
                onChange={e => handleFilterChange('approval_status', e.target.value)}
              >
                <option value="all">All Approval</option>
                <option value="approved">Approved</option>
                <option value="disapproved">Disapproved</option>
                <option value="partially_approved">Partially Approved</option>
                <option value="pending_review">Pending Review</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>
          </div>

          <div className="filter-actions" style={{ justifyContent: 'flex-start', gap: 'var(--space-3)' }}>
            <button type="button" className="btn btn-secondary" onClick={clearFilters}>
              Clear
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Loading...' : 'Search'}
            </button>
            {listings.length > 0 && (
              <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
                Showing {displayItems.length} of {listings.length} listings
              </span>
            )}
          </div>
        </div>
      </form>

      {/* Bulk Actions */}
      {displayItems.length > 0 && (
        <div className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <span className="text-sm" style={{ fontWeight: 500, color: 'var(--color-primary)' }}>
              {selectedItems.size} selected
            </span>
            <button
              className="btn btn-primary"
              onClick={handleProcessSelected}
              disabled={selectedItems.size === 0 || processing}
            >
              Process Selected
            </button>
            <select
              className="form-input"
              style={{ width: 'auto' }}
              value=""
              onChange={e => e.target.value && handleBulkStatusChange(e.target.value)}
              disabled={selectedItems.size === 0 || processing}
            >
              <option value="">Change Status...</option>
              <option value="pending">Pending</option>
              <option value="processed">Processed</option>
              <option value="skipped">Skipped</option>
              <option value="error">Error</option>
            </select>
            <button
              className="btn btn-secondary"
              onClick={handleRequestReview}
              disabled={selectedItems.size === 0 || processing}
              style={{ color: '#2563eb' }}
            >
              Request Re-review
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleBulkDelete}
              disabled={selectedItems.size === 0 || processing}
              style={{ color: 'var(--color-error)' }}
            >
              Delete Selected
            </button>
            {pendingCount > 0 && (
              <span className="text-sm text-muted">
                {pendingCount} pending listings
              </span>
            )}
          </div>
        </div>
      )}

      {/* Processing Progress */}
      {processing && progress.total > 0 && (
        <div className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>
            <span className="text-sm" style={{ fontWeight: 500 }}>
              Processing {progress.current} of {progress.total}
            </span>
            <button
              className="btn btn-sm btn-secondary"
              onClick={handleCancelOperation}
              style={{ marginLeft: 'auto' }}
            >
              Cancel
            </button>
          </div>
          <ProgressBar current={progress.current} total={progress.total} />
          <div className="text-sm text-muted" style={{ marginTop: 'var(--space-2)' }}>
            {progress.status}
          </div>
        </div>
      )}

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : !hasSearched ? (
          <div className="empty-state">
            <div className="empty-state-title">Search Google Shopping Listings</div>
            <div className="empty-state-description">Use the filters above and click "Search" to find Google Shopping listings.</div>
          </div>
        ) : displayItems.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Listings Found</div>
            <div className="empty-state-description">Try adjusting your search filters.</div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={selectedItems.size === displayItems.length && displayItems.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <SortHeader field="title">Title</SortHeader>
                <th>SKU</th>
                <SortHeader field="price" align="right">Price</SortHeader>
                <SortHeader field="quantity" align="right">Qty</SortHeader>
                <th>Google Category</th>
                <th>Condition</th>
                <th>Brand</th>
                <SortHeader field="approval_status">Approval</SortHeader>
                <SortHeader field="status">AI Status</SortHeader>
                <SortHeader field="updated_at">Updated</SortHeader>
                <th style={{ width: 120 }}>Search</th>
              </tr>
            </thead>
            <tbody>
              {displayItems.map(item => (
                <React.Fragment key={item.id}>
                  <tr className={selectedItems.has(item.id) ? 'row-selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={() => handleSelectItem(item.id)}
                      />
                    </td>
                    <td>
                      <div style={{ fontWeight: 500, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.title}
                      </div>
                    </td>
                    <td className="text-sm text-muted">{item.sku || '\u2014'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 500 }}>{formatCurrency(item.price)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <span style={{ color: item.quantity === 0 ? '#dc2626' : 'inherit', fontWeight: item.quantity === 0 ? 600 : 400 }}>
                        {item.quantity}
                      </span>
                    </td>
                    <td className="text-sm">
                      {item.google_category ? (
                        <span
                          title={item.google_category}
                          style={{ cursor: 'pointer', borderBottom: '1px dashed var(--color-text-muted)' }}
                          onClick={() => alert(item.google_category)}
                        >
                          {item.google_category.includes(' > ')
                            ? item.google_category.split(' > ').pop()
                            : item.google_category}
                        </span>
                      ) : '\u2014'}
                    </td>
                    <td className="text-sm">{item.condition_value || '\u2014'}</td>
                    <td className="text-sm">{item.brand || '\u2014'}</td>
                    <td>{getApprovalBadge(item.approval_status, item)}</td>
                    <td>{getStatusBadge(item.status)}</td>
                    <td className="text-sm text-muted">
                      {item.updated_at ? new Date(item.updated_at).toLocaleDateString('en-GB') : '\u2014'}
                    </td>
                    <td><SearchLinks title={item.title} /></td>
                  </tr>
                  {expandedIssues === item.id && <IssuesPanel item={item} />}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}

        {/* Per-page selector */}
        {displayItems.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
            <span className="text-sm text-muted">
              Showing {displayItems.length} of {listings.length} loaded
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span className="text-sm text-muted">Per page:</span>
              {[25, 50, 100, 200].map(num => (
                <button
                  key={num}
                  type="button"
                  className={`btn btn-sm ${limit === num ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setLimit(num)}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default GoogleShoppingListings
